import { api } from "../../api";

const checklistsUrl = "/v2/checklists";

// TypeScript types
export interface ChecklistTemplate {
  template_id: number;
  template_name: string;
  description?: string;
  version?: string;
  is_active: boolean;
  created_by?: number;
  updated_by?: number;
  framework?: string;
  createdAt?: string;
  updatedAt?: string;
  items?: ChecklistTemplateItem[];
  targetMappings?: ChecklistTemplateTargetMapping[];
}

export interface ChecklistTemplateItem {
  item_id: number;
  template_id: number;
  item_order: number;
  category?: string;
  title: string;
  description?: string;
  test_objective?: string;
  expected_evidence?: string;
  references?: string;
  requirement_id?: string;
  l1: boolean;
  l2: boolean;
  l3: boolean;
  is_web?: boolean;
  is_api?: boolean;
  is_mobile?: boolean;
  is_infra?: boolean;
  is_required: boolean;
}

export interface ChecklistTemplateTargetMapping {
  mapping_id: number;
  template_id: number;
  target_type: string;
}

export interface ProgramChecklist {
  program_checklist_id: number;
  run_checklist_id?: number;
  program_id: number;
  run_id?: number;
  template_id: number;
  checklist_name: string;
  framework?: string;
  description?: string;
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "BLOCKED";
  completion_percentage: number | string; // Sequelize returns DECIMAL as string
  linked_by?: number;
  linked_at?: string;
  is_frozen?: boolean;
  is_admin_reviewed?: boolean;
  frozen_by?: number;
  frozen_at?: string;
  createdAt?: string;
  updatedAt?: string;
  items?: ProgramChecklistItem[];
  template?: ChecklistTemplate;
}

export interface ProgramChecklistItem {
  program_item_id: number;
  program_checklist_id: number;
  template_item_id: number;
  item_order: number;
  category?: string;
  title: string;
  description?: string;
  test_objective?: string;
  expected_evidence?: string;
  references?: string;
  requirement_id?: string;
  l1: boolean;
  l2: boolean;
  l3: boolean;
  is_web?: boolean;
  is_api?: boolean;
  is_mobile?: boolean;
  is_infra?: boolean;
  is_required: boolean;
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "NOT_APPLICABLE" | "FAILED";
  actual_evidence?: string;
  evidence_files?: string[];
  notes?: string;
  assigned_to?: number[];
  tested_by?: number;
  tested_at?: string;
  reviewed_by?: number;
  reviewed_at?: string;
  bug_found?: boolean;
  linked_report_ids?: number[];
  version?: number;
  tester?: {
    user_id: number;
    username: string;
    display_name: string;
    email: string;
  };
  reviewer?: {
    user_id: number;
    username: string;
    display_name: string;
    email: string;
  };
}

export interface ChecklistProgress {
  total_items: number;
  not_started: number;
  in_progress: number;
  completed: number;
  not_applicable: number;
  failed: number;
  completion_percentage: number;
}

export interface CategoryStatus {
  total: number;
  completed: number;
  hasLinkedReports: boolean;
  status: "PASSED" | "FAILED" | "IN_PROGRESS" | "NOT_STARTED";
}

export interface BusinessChecklistResponse {
  checklist: ProgramChecklist;
  items: ProgramChecklistItem[];
  categoryStatus: Record<string, CategoryStatus>;
  statistics: {
    total: number;
    completed: number;
    pending: number;
    notStarted: number;
    completionPercentage: number;
  };
}

/**
 * The Checklist API slice definition using RTK Query
 */
export const checklistApi = api.injectEndpoints({
  endpoints: builder => ({
    // ============================================
    // CHECKLIST TEMPLATE ENDPOINTS (ADMIN ONLY)
    // ============================================

    /**
     * Get all checklist templates
     */
    getChecklistTemplates: builder.query<
      { success: boolean; data: ChecklistTemplate[]; count: number },
      { active_only?: boolean } | void
    >({
      query: params => ({
        url: `${checklistsUrl}/templates`,
        method: "get",
        params
      }),
      providesTags: ["ChecklistTemplates"]
    }),

    /**
     * Get a single checklist template by ID
     */
    getChecklistTemplate: builder.query<
      { success: boolean; data: ChecklistTemplate },
      number
    >({
      query: id => ({
        url: `${checklistsUrl}/templates/${id}`,
        method: "get"
      }),
      providesTags: (_, __, id) => [{ type: "ChecklistTemplate", id }]
    }),

    /**
     * Create a new checklist template
     */
    createChecklistTemplate: builder.mutation<
      { success: boolean; message: string; data: ChecklistTemplate },
      {
        template_name: string;
        description?: string;
        version?: string;
        is_active?: boolean;
        framework?: string;
        target_types?: string[];
        items?: Partial<ChecklistTemplateItem>[];
      }
    >({
      query: data => ({
        url: `${checklistsUrl}/templates`,
        method: "post",
        data
      }),
      invalidatesTags: ["ChecklistTemplates"]
    }),

    /**
     * Update a checklist template
     */
    updateChecklistTemplate: builder.mutation<
      { success: boolean; message: string; data: ChecklistTemplate },
      {
        id: number;
        template_name?: string;
        description?: string;
        version?: string;
        is_active?: boolean;
        framework?: string;
        target_types?: string[];
      }
    >({
      query: ({ id, ...data }) => ({
        url: `${checklistsUrl}/templates/${id}`,
        method: "put",
        data
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "ChecklistTemplate", id },
        "ChecklistTemplates"
      ]
    }),

    /**
     * Delete a checklist template
     */
    deleteChecklistTemplate: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: id => ({
        url: `${checklistsUrl}/templates/${id}`,
        method: "delete"
      }),
      invalidatesTags: ["ChecklistTemplates"]
    }),

    /**
     * Add items to a template
     */
    addTemplateItems: builder.mutation<
      { success: boolean; message: string; data: ChecklistTemplateItem[] },
      {
        templateId: number;
        items: Partial<ChecklistTemplateItem>[];
      }
    >({
      query: ({ templateId, items }) => ({
        url: `${checklistsUrl}/templates/${templateId}/items`,
        method: "post",
        data: { items }
      }),
      invalidatesTags: (_, __, { templateId }) => [
        { type: "ChecklistTemplate", id: templateId },
        "ChecklistTemplates"
      ]
    }),

    /**
     * Update a template item
     */
    updateTemplateItem: builder.mutation<
      { success: boolean; message: string; data: ChecklistTemplateItem },
      {
        templateId: number;
        itemId: number;
        data: Partial<ChecklistTemplateItem>;
      }
    >({
      query: ({ templateId, itemId, data }) => ({
        url: `${checklistsUrl}/templates/${templateId}/items/${itemId}`,
        method: "put",
        data
      }),
      invalidatesTags: (_, __, { templateId }) => [
        { type: "ChecklistTemplate", id: templateId },
        "ChecklistTemplates"
      ]
    }),

    /**
     * Delete a template item
     */
    deleteTemplateItem: builder.mutation<
      { success: boolean; message: string },
      {
        templateId: number;
        itemId: number;
      }
    >({
      query: ({ templateId, itemId }) => ({
        url: `${checklistsUrl}/templates/${templateId}/items/${itemId}`,
        method: "delete"
      }),
      invalidatesTags: (_, __, { templateId }) => [
        { type: "ChecklistTemplate", id: templateId },
        "ChecklistTemplates"
      ]
    }),

    /**
     * Import template items from JSON/CSV file
     */
    importTemplateItems: builder.mutation<
      {
        success: boolean;
        message: string;
        data: {
          imported_count: number;
          items: ChecklistTemplateItem[]
        }
      },
      {
        templateId: number;
        file: File;
      }
    >({
      query: ({ templateId, file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `${checklistsUrl}/templates/${templateId}/import`,
          method: "post",
          data: formData,
          headers: {
            // Don't set Content-Type, let the browser set it with boundary for multipart/form-data
          }
        };
      },
      invalidatesTags: (_, __, { templateId }) => [
        { type: "ChecklistTemplate", id: templateId },
        "ChecklistTemplates"
      ]
    }),

    // ============================================
    // PROGRAM CHECKLIST ENDPOINTS
    // ============================================

    /**
     * Get all checklists for a program
     */
    getProgramChecklists: builder.query<
      { success: boolean; data: ProgramChecklist[]; count: number },
      number
    >({
      query: programId => ({
        url: `${checklistsUrl}/programs/${programId}/checklists`,
        method: "get"
      }),
      providesTags: (_, __, programId) => [
        { type: "ProgramChecklists", id: programId }
      ]
    }),

    /**
     * Get a specific checklist for a program
     */
    getProgramChecklist: builder.query<
      { success: boolean; data: ProgramChecklist },
      { programId: number; checklistId: number }
    >({
      query: ({ programId, checklistId }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/${checklistId}`,
        method: "get"
      }),
      providesTags: (_, __, { programId, checklistId }) => [
        { type: "ProgramChecklist", id: checklistId },
        { type: "ProgramChecklists", id: programId }
      ]
    }),

    /**
     * Manually link a checklist template to a program (ADMIN only)
     */
    linkChecklistToProgram: builder.mutation<
      { success: boolean; message: string; data: ProgramChecklist },
      { programId: number; templateId?: number; custom_name?: string }
    >({
      query: ({ programId, templateId, custom_name }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/link`,
        method: "post",
        data: { 
          templateId: Number(templateId) || undefined,
          template_id: Number(templateId) || undefined,
          custom_name 
        }
      }),
      invalidatesTags: (_, __, { programId }) => [
        { type: "ProgramChecklists", id: programId },
        { type: "Program", id: programId }
      ]
    }),

    /**
     * Unlink a checklist from a program (ADMIN only)
     */
    unlinkChecklistFromProgram: builder.mutation<
      { success: boolean; message: string },
      { programId: number; checklistId: number }
    >({
      query: ({ programId, checklistId }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/${checklistId}`,
        method: "delete"
      }),
      invalidatesTags: (_, __, { programId }) => [
        { type: "ProgramChecklists", id: programId },
        { type: "Program", id: programId }
      ]
    }),

    /**
     * Unlink a checklist from a run (ADMIN only)
     */
    unlinkChecklistFromRun: builder.mutation<
      { success: boolean; message: string },
      { runId: number; checklistId: number }
    >({
      query: ({ runId, checklistId }) => ({
        url: `${checklistsUrl}/runs/${runId}/checklists/${checklistId}`,
        method: "delete"
      }),
      invalidatesTags: (_, __, { runId }) => [
        { type: "RunChecklists", id: runId }
      ]
    }),

    /**
     * Add items to a program checklist (ADMIN and QA only)
     */
    addChecklistItems: builder.mutation<
      { success: boolean; message: string; data: ProgramChecklistItem[] },
      {
        programId: number;
        checklistId: number;
        items: Partial<ProgramChecklistItem>[];
      }
    >({
      query: ({ programId, checklistId, items }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/${checklistId}/items`,
        method: "post",
        data: { items }
      }),
      invalidatesTags: (_, __, { programId, checklistId }) => [
        { type: "ProgramChecklist", id: checklistId },
        { type: "ProgramChecklists", id: programId }
      ]
    }),

    deleteChecklistItems: builder.mutation<
      { success: boolean; message: string },
      {
        programId: number;
        checklistId: number;
        itemIds: number[];
      }
    >({
      query: ({ programId, checklistId, itemIds }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/${checklistId}/items/bulk`,
        method: "delete",
        data: { itemIds }
      }),
      invalidatesTags: (_, __, { programId, checklistId }) => [
        { type: "ProgramChecklist", id: checklistId },
        { type: "ProgramChecklists", id: programId }
      ]
    }),

    /**
     * Bulk update status for program checklist items
     */
    bulkUpdateProgramStatus: builder.mutation<
      { success: boolean; message: string },
      {
        programId: number;
        checklistId: number;
        itemIds: number[];
        status: string;
      }
    >({
      query: ({ programId, checklistId, itemIds, status }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/${checklistId}/items/bulk/status`,
        method: "put",
        data: { itemIds, status }
      }),
      invalidatesTags: (_, __, { programId, checklistId }) => [
        { type: "ProgramChecklist", id: checklistId },
        { type: "ProgramChecklists", id: programId }
      ]
    }),

    /**
     * Update a checklist item (ADMIN and QA can update)
     */
    updateChecklistItem: builder.mutation<
      { success: boolean; message: string; data: ProgramChecklistItem },
      {
        programId: number;
        checklistId: number;
        itemId: number;
        data: Partial<ProgramChecklistItem>;
      }
    >({
      query: ({ programId, checklistId, itemId, data }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/${checklistId}/items/${itemId}`,
        method: "put",
        data
      }),
      invalidatesTags: (_, __, { programId, checklistId }) => [
        { type: "ProgramChecklist", id: checklistId },
        { type: "ProgramChecklists", id: programId }
      ]
    }),

    /**
     * Delete a checklist item (ADMIN and QA only)
     */
    deleteChecklistItem: builder.mutation<
      { success: boolean; message: string },
      {
        programId: number;
        checklistId: number;
        itemId: number;
      }
    >({
      query: ({ programId, checklistId, itemId }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/${checklistId}/items/${itemId}`,
        method: "delete"
      }),
      invalidatesTags: (_, __, { programId, checklistId }) => [
        { type: "ProgramChecklist", id: checklistId },
        { type: "ProgramChecklists", id: programId }
      ]
    }),

    assignResearchersToItem: builder.mutation<
      { success: boolean; message: string; data: ProgramChecklistItem },
      {
        programId: number;
        checklistId: number;
        itemId: number;
        assigned_to: number[] | null;
      }
    >({
      query: ({ programId, checklistId, itemId, assigned_to }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/${checklistId}/items/${itemId}/assign`,
        method: "put",
        data: { assigned_to }
      }),
      invalidatesTags: (_, __, { programId, checklistId }) => [
        { type: "ProgramChecklist", id: checklistId },
        { type: "ProgramChecklists", id: programId }
      ]
    }),

    /**
     * Bulk assign researchers to program test cases (ADMIN/QA only)
     */
    bulkAssignResearchersToItem: builder.mutation<
      { success: boolean; message: string },
      {
        programId: number;
        checklistId: number;
        itemIds: number[];
        assigned_to: number[] | null;
      }
    >({
      query: ({ programId, checklistId, itemIds, assigned_to }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/${checklistId}/items/bulk/assign`,
        method: "put",
        data: { itemIds, assigned_to }
      }),
      invalidatesTags: (_, __, { programId, checklistId }) => [
        { type: "ProgramChecklist", id: checklistId },
        { type: "ProgramChecklists", id: programId }
      ]
    }),

    /**
     * Get checklist progress/stats
     */
    getChecklistProgress: builder.query<
      { success: boolean; data: ChecklistProgress },
      { programId: number; checklistId: number; runId?: number }
    >({
      query: ({ programId, checklistId }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/${checklistId}/progress`,
        method: "get"
      }),
      providesTags: (_, __, { checklistId, runId }) => [
        { type: "ProgramChecklist", id: checklistId },
        ...(runId ? [{ type: "RunChecklists" as const, id: runId }] : [])
      ]
    }),

    /**
     * Freeze a program checklist (QA/ADMIN only)
     */
    freezeProgramChecklist: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: checklistId => ({
        url: `${checklistsUrl}/program-checklists/${checklistId}/freeze`,
        method: "post"
      }),
      invalidatesTags: (_, __, checklistId) => [
        { type: "ProgramChecklist", id: checklistId },
        { type: "RunChecklist", id: checklistId },
        "ProgramChecklists",
        "RunChecklists"
      ]
    }),

    /**
     * Unfreeze a program checklist (QA/ADMIN only)
     */
    unfreezeProgramChecklist: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: checklistId => ({
        url: `${checklistsUrl}/program-checklists/${checklistId}/unfreeze`,
        method: "post"
      }),
      invalidatesTags: (_, __, checklistId) => [
        { type: "ProgramChecklist", id: checklistId },
        { type: "RunChecklist", id: checklistId },
        "ProgramChecklists",
        "RunChecklists"
      ]
    }),

    /**
     * Finalize admin review for a checklist (program-level or run-level)
     */
    finalizeAdminReview: builder.mutation<
      { success: boolean; message: string },
      { programId: number; checklistId: number; runId?: number }
    >({
      query: ({ programId, checklistId, runId }) => ({
        url: runId
          ? `${checklistsUrl}/runs/${runId}/checklists/${checklistId}/review`
          : `${checklistsUrl}/programs/${programId}/checklists/${checklistId}/review`,
        method: "post"
      }),
      invalidatesTags: (_, __, { programId, checklistId, runId }) => [
        ...(runId
          ? [
              { type: "RunChecklist" as const, id: checklistId },
              { type: "RunChecklists" as const, id: runId }
            ]
          : [
              { type: "ProgramChecklist" as const, id: checklistId },
              { type: "ProgramChecklists" as const, id: programId }
            ])
      ]
    }),

    /**
     * Merge items from another template into a program checklist
     */
    mergeTemplateToProgram: builder.mutation<
      { success: boolean; message: string },
      { programId: number; checklistId: number; templateId: number }
    >({
      query: ({ programId, checklistId, templateId }) => ({
        url: `${checklistsUrl}/programs/${programId}/checklists/${checklistId}/merge`,
        method: "post",
        data: { templateId }
      }),
      invalidatesTags: (_, __, { programId, checklistId }) => [
        { type: "ProgramChecklist", id: checklistId },
        { type: "ProgramChecklists", id: programId }
      ]
    }),

    /**
     * Get all checklists for a run (Snapshot)
     */
    getRunChecklists: builder.query<
      { success: boolean; data: ProgramChecklist[]; count: number },
      number
    >({
      query: runId => ({
        url: `${checklistsUrl}/runs/${runId}/checklists`,
        method: "get"
      }),
      providesTags: (_, __, runId) => [
        { type: "RunChecklists", id: runId }
      ]
    }),

    /**
     * Update a run checklist item
     */
    updateRunChecklistItem: builder.mutation<
      { success: boolean; message: string; data: ProgramChecklistItem },
      {
        runId: number;
        itemId: number;
        data: Partial<ProgramChecklistItem>;
      }
    >({
      query: ({ runId, itemId, data }) => ({
        url: `${checklistsUrl}/runs/${runId}/items/${itemId}`,
        method: "put",
        data
      }),
      invalidatesTags: (_, __, { runId }) => [
        { type: "RunChecklists", id: runId }
      ]
    }),

    /**
     * Delete a run checklist item
     */
    deleteRunChecklistItem: builder.mutation<
      { success: boolean; message: string },
      {
        runId: number;
        itemId: number;
      }
    >({
      query: ({ runId, itemId }) => ({
        url: `${checklistsUrl}/runs/${runId}/items/${itemId}`,
        method: "delete"
      }),
      invalidatesTags: (_, __, { runId }) => [
        { type: "RunChecklists", id: runId }
      ]
    }),

    /**
     * Bulk delete run checklist items
     */
    deleteRunChecklistItems: builder.mutation<
      { success: boolean; message: string },
      {
        runId: number;
        itemIds: number[];
      }
    >({
      query: ({ runId, itemIds }) => ({
        url: `${checklistsUrl}/runs/${runId}/items/bulk`,
        method: "delete",
        data: { itemIds }
      }),
      invalidatesTags: (_, __, { runId }) => [
        { type: "RunChecklists", id: runId }
      ]
    }),

    /**
     * Bulk update status for run checklist items
     */
    bulkUpdateRunStatus: builder.mutation<
      { success: boolean; message: string },
      {
        runId: number;
        itemIds: number[];
        status: string;
      }
    >({
      query: ({ runId, itemIds, status }) => ({
        url: `${checklistsUrl}/runs/${runId}/items/bulk/status`,
        method: "put",
        data: { itemIds, status }
      }),
      invalidatesTags: (_, __, { runId }) => [
        { type: "RunChecklists", id: runId }
      ]
    }),

    /**
     * Bulk assign researchers to run checklist items (ADMIN/QA only)
     */
    bulkAssignResearchersToRunItem: builder.mutation<
      { success: boolean; message: string },
      {
        runId: number;
        itemIds: number[];
        assigned_to: number[] | null;
      }
    >({
      query: ({ runId, itemIds, assigned_to }) => ({
        url: `${checklistsUrl}/runs/${runId}/items/bulk/assign`,
        method: "put",
        data: { itemIds, assigned_to }
      }),
      invalidatesTags: (_, __, { runId }) => [
        { type: "RunChecklists", id: runId }
      ]
    }),

    /**
     * Get a specific checklist for a run (Snapshot)
     */
    getRunChecklist: builder.query<
      { success: boolean; data: ProgramChecklist },
      { runId: number; checklistId: number }
    >({
      query: ({ runId, checklistId }) => ({
        url: `${checklistsUrl}/runs/${runId}/checklists/${checklistId}`,
        method: "get"
      }),
      providesTags: (_, __, { runId, checklistId }) => [
        { type: "RunChecklist", id: checklistId },
        { type: "RunChecklists", id: runId }
      ]
    }),

    /**
     * Add items to a run checklist
     */
    addRunChecklistItems: builder.mutation<
      { success: boolean; message: string; data: ProgramChecklistItem[] },
      {
        runId: number;
        checklistId: number;
        items: Partial<ProgramChecklistItem>[];
      }
    >({
      query: ({ runId, checklistId, items }) => ({
        url: `${checklistsUrl}/runs/${runId}/checklists/${checklistId}/items`,
        method: "post",
        data: { items }
      }),
      invalidatesTags: (_, __, { runId }) => [
        { type: "RunChecklists", id: runId }
      ]
    }),

    /**
     * Merge items from another template into a run checklist
     */
    mergeTemplateToRun: builder.mutation<
      { success: boolean; message: string },
      { runId: number; checklistId: number; templateId: number }
    >({
      query: ({ runId, checklistId, templateId }) => ({
        url: `${checklistsUrl}/runs/${runId}/checklists/${checklistId}/merge`,
        method: "post",
        data: { templateId }
      }),
      invalidatesTags: (_, __, { runId, checklistId }) => [
        { type: "RunChecklist", id: checklistId },
        { type: "RunChecklists", id: runId }
      ]
    }),
    /**
     * Assign researchers to a run checklist item
     */
    assignResearchersToRunItem: builder.mutation<
      { success: boolean; message: string; data: ProgramChecklistItem },
      {
        runId: number;
        itemId: number;
        assigned_to: number[] | null;
      }
    >({
      query: ({ runId, itemId, assigned_to }) => ({
        url: `${checklistsUrl}/runs/${runId}/items/${itemId}/assign`,
        method: "put",
        data: { assigned_to }
      }),
      invalidatesTags: (_, __, { runId }) => [
        { type: "RunChecklists", id: runId }
      ]
    }),
    linkTemplateToRun: builder.mutation<any, { runId: number; templateId?: number; programChecklistId?: number }>({
      query: ({ runId, templateId, programChecklistId }) => ({
        url: `${checklistsUrl}/runs/${runId}/templates/${templateId || 0}/link`,
        method: "POST",
        data: { templateId, programChecklistId }
      }),
      invalidatesTags: (_, __, { runId }) => [{ type: "RunChecklists", id: runId }]
    }),
    /**
     * Generate master checklist items based on filters
     */
    generateMasterItems: builder.mutation<
      { success: boolean; message: string; data: ProgramChecklist },
      {
        programId: number;
        runId?: number;
        templateId: number | number[];
        asvsLevel?: number;
        categories?: string[];
        targetType: "Web" | "API" | "Web+API";
        ctx?: any;
        checklistName?: string;
        checklistDescription?: string;
        framework?: string;
      }
    >({
      query: (data) => ({
        url: `${checklistsUrl}/programs/${data.programId}/generate`,
        method: "POST",
        data
      }),
      invalidatesTags: (_, __, { programId, runId }) => [
        { type: "ProgramChecklists", id: programId },
        { type: "Program", id: programId },
        ...(runId ? [{ type: "RunChecklists" as const, id: runId }] : [])
      ]
    })
  })
});

// Export hooks for usage in functional components
export const {
  useGetChecklistTemplatesQuery,
  useGetChecklistTemplateQuery,
  useCreateChecklistTemplateMutation,
  useUpdateChecklistTemplateMutation,
  useDeleteChecklistTemplateMutation,
  useAddTemplateItemsMutation,
  useUpdateTemplateItemMutation,
  useDeleteTemplateItemMutation,
  useImportTemplateItemsMutation,
  useGetProgramChecklistsQuery,
  useGetProgramChecklistQuery,
  useLinkChecklistToProgramMutation,
  useUnlinkChecklistFromProgramMutation,
  useAssignResearchersToItemMutation,
  useAddChecklistItemsMutation,
  useDeleteChecklistItemsMutation,
  useUpdateChecklistItemMutation,
  useDeleteChecklistItemMutation,
  useGetChecklistProgressQuery,
  useFreezeProgramChecklistMutation,
  useUnfreezeProgramChecklistMutation,
  useFinalizeAdminReviewMutation,
  useMergeTemplateToProgramMutation,
  useGetRunChecklistsQuery,
  useGetRunChecklistQuery,
  useUpdateRunChecklistItemMutation,
  useDeleteRunChecklistItemMutation,
  useAddRunChecklistItemsMutation,
  useDeleteRunChecklistItemsMutation,
  useMergeTemplateToRunMutation,
  useLinkTemplateToRunMutation,
  useUnlinkChecklistFromRunMutation,
  useAssignResearchersToRunItemMutation,
  useBulkUpdateRunStatusMutation,
  useBulkAssignResearchersToRunItemMutation,
  useBulkAssignResearchersToItemMutation,
  useBulkUpdateProgramStatusMutation,
  useGenerateMasterItemsMutation
} = checklistApi;
